import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import Loader from "components/DesignSystem/Loader/loader";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { DeleteOpenItem } from "components/Transaction/RequestInfo";
import { AnimatePresence, motion } from "framer-motion";
import { useWindowSize } from "hooks/useWindowSize";
import { FC, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetOpenItemsQuery } from "store/apis/openItem";
import { useChannelStateContext } from "stream-chat-react";
import { OpenItem } from "types/Models/openItem";
import { pluralize } from "utils/pluralize";
import { MobileOpenItemList } from "./MobileOpenItemList";
import { OpenItemClarificationInput } from "./OpenItemClarificationInput";
import { OpenItemInvoiceInput } from "./OpenItemInvoiceInput";

const defaultArray: any[] = [];

const matchesSearch = (item: OpenItem, query: string) => {
  const q = query.toLowerCase();
  return (
    item.description?.toLowerCase().includes(q) ||
    item.bank_name?.toLowerCase().includes(q) ||
    item.merchant?.toLowerCase().includes(q) ||
    item.information_request?.toLowerCase().includes(q) ||
    item.amount?.toLowerCase().includes(q) ||
    item.date?.toLowerCase().includes(q)
  );
};

const COLUMN_IDS = {
  DATE: "date",
  VENDOR: "vendor",
  BANK_NAME: "bank_name",
  DESCRIPTION: "description",
  AMOUNT: "amount",
  INFORMATION_REQUEST: "information_request",
  DELETE: "Delete",
  INVOICE: "invoice",
  CLARIFICATION: "clarification",
} as const;

const ADD_OPEN_ITEM_COLUMN_SIZING = {
  [COLUMN_IDS.DATE]: 12,
  [COLUMN_IDS.VENDOR]: 15,
  [COLUMN_IDS.BANK_NAME]: 12,
  [COLUMN_IDS.DESCRIPTION]: 18,
  [COLUMN_IDS.AMOUNT]: 12,
  [COLUMN_IDS.INFORMATION_REQUEST]: 30,
};
const CLARIFY_COLUMN_SIZING = {};

const columnHelper = createColumnHelper<OpenItem>();

const columns = [
  columnHelper.accessor("date", {
    id: COLUMN_IDS.DATE,
    header: "Date",
    size: 10,
    cell: (info) => info.getValue(),
  }),

  columnHelper.accessor("merchant", {
    id: COLUMN_IDS.VENDOR,
    header: "Vendor",
    size: 12,
    cell: (info) => {
      const merchant = info.getValue();
      const logo = info.row.original.logo;
      if (!merchant) return <span className="t-text-text-30">-</span>;
      return (
        <div className="t-flex t-gap-2 t-items-center">
          <Avatar src={logo || ""} alt={merchant} />
          <div className="t-text-subtext t-text-text-60 t-break-words">
            {merchant}
          </div>
        </div>
      );
    },
  }),

  columnHelper.accessor("bank_name", {
    id: COLUMN_IDS.BANK_NAME,
    header: "Bank",
    size: 10,
    cell: (info) => (
      <div className="t-break-words">{info.getValue() || "-"}</div>
    ),
  }),

  columnHelper.accessor("description", {
    id: COLUMN_IDS.DESCRIPTION,
    header: "Description",
    size: 12,
    cell: (info) => (
      <div className="t-break-words">{info.getValue() || "-"}</div>
    ),
  }),

  columnHelper.accessor("amount", {
    id: COLUMN_IDS.AMOUNT,
    header: "Amount",
    size: 10,
    cell: (info) => {
      const { amount } = info.row.original;

      if (!Boolean(info.getValue())) {
        return "-";
      }

      if (isNaN(Number(amount))) {
        return amount;
      }

      return <AmountSuperScript amount={Number(amount)} />;
    },
  }),

  columnHelper.accessor("information_request", {
    id: COLUMN_IDS.INFORMATION_REQUEST,
    header: "Information Request",
    size: 15,
    cell: (info) => (
      <div className="t-break-words">{info.getValue() || "-"}</div>
    ),
  }),

  columnHelper.accessor("uuid", {
    id: COLUMN_IDS.DELETE,
    header: "",
    size: 5,
    cell: DeleteOpenItem,
  }),

  columnHelper.accessor("invoice", {
    id: COLUMN_IDS.INVOICE,
    header: "Invoice",
    size: 8,
    cell: (info) => (
      <OpenItemInvoiceInput openItem={info.row.original} variant="icon" />
    ),
  }),

  columnHelper.accessor("clarification", {
    id: COLUMN_IDS.CLARIFICATION,
    header: "Clarification",
    size: 18,
    cell: (info) => <OpenItemClarificationInput openItem={info.row.original} />,
  }),
];

export const OpenItemTable: FC<{ csvId: string; addOpenItem?: boolean }> = ({
  csvId,
  addOpenItem,
}) => {
  const [search, setSearch] = useState("");
  const { isMobile } = useWindowSize();
  const { channel } = useChannelStateContext();
  const groupId = channel?.data?.group_uuid as string;

  const { data: uploadedCSV, isLoading } = useGetOpenItemsQuery(
    { csvId, groupId },
    { skip: !groupId || !csvId, refetchOnMountOrArgChange: true }
  );

  const { open_items } = uploadedCSV || {};

  const filteredItems = search
    ? (open_items || defaultArray).filter((item) => matchesSearch(item, search))
    : open_items || defaultArray;

  const isAddOpenItem = Boolean(addOpenItem);

  const table = useReactTable({
    data: filteredItems,
    columns,
    state: {
      columnVisibility: {
        [COLUMN_IDS.DELETE]: isAddOpenItem,
        [COLUMN_IDS.INVOICE]: !isAddOpenItem,
        [COLUMN_IDS.CLARIFICATION]: !isAddOpenItem,
      },
      columnSizing: isAddOpenItem
        ? ADD_OPEN_ITEM_COLUMN_SIZING
        : CLARIFY_COLUMN_SIZING,
    },
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  if (isLoading) {
    return <Loader />;
  }

  const totalCount = open_items?.length ?? 0;
  const filteredCount = filteredItems.length;
  const showMobileList = isMobile && !addOpenItem;

  const itemCountLabel = search
    ? `${filteredCount} of ${pluralize(totalCount, "item", "items")}`
    : pluralize(totalCount, "item", "items");

  const searchBar = (
    <div className="t-flex t-items-center t-justify-between">
      <Search
        placeholder="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="t-w-72 sm:t-w-full"
      />
      <span className="t-text-subtext t-text-text-30 sm:t-hidden">
        {itemCountLabel}
      </span>
    </div>
  );

  if (showMobileList) {
    return (
      <div className="t-flex t-flex-col t-gap-3">
        {searchBar}
        <MobileOpenItemList openItems={filteredItems} />
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {searchBar}
      <Table.Container
        sticky={true}
        className="t-overflow-y-auto t-max-h-[55vh]"
      >
        <Table.Content>
          <Table.Head>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.HeadCell
                    key={header.id}
                    style={{ width: `${header.getSize()}%` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                ))}
              </Table.Row>
            ))}
          </Table.Head>
          <Table.Body>
            <AnimatePresence>
              {table.getRowModel().rows.map((row) => (
                <motion.tr
                  layout
                  key={row.original.uuid}
                  transition={{ duration: 0.1, ease: "easeOut" }}
                  animate={{ x: 0 }}
                  exit={{ x: -1000 }}
                  className="t-px-3 t-border-solid t-border-neutral-0 t-border-b t-border-0 t-text-body-sm"
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}%` }}
                      className="t-py-3 t-px-2"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </motion.tr>
              ))}
            </AnimatePresence>
          </Table.Body>
        </Table.Content>
      </Table.Container>
    </div>
  );
};
